import { wizardConfig } from '../config/wizard-config';
import { authorizedFetch } from './session';
import { readStoredReplyTranslateLanguages } from './reply-translate-languages';

const API_BASE = wizardConfig.apiBaseUrl;

async function safeJson(response: Response): Promise<Record<string, unknown>> {
  try {
    return await response.json();
  } catch {
    throw new Error(`Unexpected server response (HTTP ${response.status})`);
  }
}

/** Throws SessionExpiredError (dialog shown) or GenesysUnavailableError instead of returning those. */
async function fetchWithAuth(
  url: string,
  options: RequestInit = {},
): Promise<Response> {
  return authorizedFetch(url, options);
}

export interface RegisterOrgPayload {
  organization: string;
  region: string;
  client_id: string;
  client_secret: string;
  dataRegion: string;
  genesysOrgId: string;
  scanEnabled?: boolean;
  aiActionsEnabled?: boolean;
  gcLangTag?: string;
  gcTargetEnv?: string;
}

/**
 * Register organization with MediaBriz backend via wizard proxy.
 * Returns { status: true } on success or { status: false, cause: string } on failure.
 */
export async function registerOrganization(
  payload: RegisterOrgPayload,
): Promise<{ status: boolean; cause?: string }> {
  const response = await fetchWithAuth(`${API_BASE}/api/register-org`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  const body = await safeJson(response);

  if (response.ok && body.success) {
    return { status: true };
  }

  if (response.status === 409) {
    return { status: false, cause: 'Organization already exists' };
  }

  return {
    status: false,
    cause: (body.message as string) || (body.error as string) || `Registration failed (HTTP ${response.status})`,
  };
}

/**
 * Check if organization already exists in MediaBriz backend.
 * Uses genesysOrgId for reliable lookup.
 * Returns exists + hasCredentials to distinguish fully provisioned orgs from pre-created ones.
 */
export async function checkOrganization(
  genesysOrgId: string,
  genesysRegion: string,
): Promise<{
  exists: boolean;
  hasCredentials: boolean;
  organization?: string;
  region?: string;
  scanEnabled?: boolean;
  /** Operator flag. Absent means Login Limit is off. */
  loginLimitEnabled?: boolean;
  aiActionsEnabled?: boolean;
  /** Operator flag. Absent means on; false means the org has Login Limit only. */
  conversationsEnabled?: boolean;
}> {
  const params = new URLSearchParams({ genesysOrgId, genesysRegion });
  const response = await fetchWithAuth(`${API_BASE}/api/check-org?${params.toString()}`);

  if (response.status === 200) {
    const body = await safeJson(response);
    const org = (body.organization as Record<string, unknown>) || body;
    return {
      exists: true,
      hasCredentials: org.hasCredentials !== false,
      organization: org.organization as string | undefined,
      region: org.region as string | undefined,
      // Toolbar already returns scanEnabled on the org record; default false
      // when absent so Settings never appears for unknown Guard state.
      scanEnabled: org.scanEnabled === true,
      loginLimitEnabled: org.loginLimitEnabled === true,
      // Conversation Actions is default-on per contract: missing = enabled.
      aiActionsEnabled: org.aiActionsEnabled !== false,
      conversationsEnabled: org.conversationsEnabled !== false,
    };
  }
  if (response.status === 404) {
    // Not in the Organizations table. Callers must still fail closed (Landing or
    // error, never Welcome) if Genesys already has Mediabriz wizard OAuth clients.
    return { exists: false, hasCredentials: false, scanEnabled: false, loginLimitEnabled: false, aiActionsEnabled: false, conversationsEnabled: true };
  }

  throw new Error(`Check org failed (HTTP ${response.status})`);
}

export interface OrgThreatSettings {
  threatNotifyEnabled: boolean;
  threatNotifyEmail: string;
  scanEnabled: boolean;
  /** Effective secure media delivery: only true when Guard is also on. Read-only. */
  mediaDeliveryEnabled: boolean;
  /** null: agents keep the full reply-translation menu. */
  replyTranslateLanguages: string[] | null;
  /** Other Conversations also lists conversations with the same external tag. Missing is off. */
  historyTagSearchEnabled: boolean;
  /** Live call transcription. Missing is on. */
  callTranscriptionEnabled: boolean;
  /** PDF of a call transcript. Missing is on. Also needs the browser's Export to PDF switch. */
  callTranscriptExportEnabled: boolean;
}

/** Load organization settings for the org. */
export async function getOrgSettings(
  genesysOrgId: string,
  genesysRegion: string,
): Promise<OrgThreatSettings> {
  const params = new URLSearchParams({ genesysOrgId, genesysRegion });
  const response = await fetchWithAuth(`${API_BASE}/api/org-settings?${params.toString()}`);
  const body = await safeJson(response);

  if (!response.ok) {
    throw new Error(
      (body.message as string) || (body.error as string) || `Load settings failed (HTTP ${response.status})`,
    );
  }

  return {
    threatNotifyEnabled: body.threatNotifyEnabled === true,
    threatNotifyEmail: typeof body.threatNotifyEmail === 'string' ? body.threatNotifyEmail : '',
    scanEnabled: body.scanEnabled === true,
    mediaDeliveryEnabled: body.scanEnabled === true && body.mediaDeliveryEnabled === true,
    replyTranslateLanguages: readStoredReplyTranslateLanguages(body.replyTranslateLanguages),
    historyTagSearchEnabled: body.historyTagSearchEnabled === true,
    callTranscriptionEnabled: body.callTranscriptionEnabled !== false,
    callTranscriptExportEnabled: body.callTranscriptExportEnabled !== false,
  };
}

/**
 * Persist one organization-settings write via wizard → toolbar admin API.
 * Threat-email, reply-translation and previous-conversations are separate
 * bodies so one save does not clear another. A reply-translation body sends
 * null when unrestricted.
 */
export async function saveOrgSettings(
  genesysOrgId: string,
  genesysRegion: string,
  settings:
    | { threatNotifyEnabled: boolean; threatNotifyEmail: string }
    | { replyTranslateLanguages: string[] | null }
    | { historyTagSearchEnabled: boolean }
    | { callTranscriptionEnabled?: boolean; callTranscriptExportEnabled?: boolean },
): Promise<{ status: boolean; cause?: string; code?: string }> {
  const params = new URLSearchParams({ genesysOrgId, genesysRegion });
  const response = await fetchWithAuth(`${API_BASE}/api/org-settings?${params.toString()}`, {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
  const body = await safeJson(response);

  if (response.ok && body.success) {
    return { status: true };
  }

  return {
    status: false,
    ...(typeof body.error === 'string' ? { code: body.error } : {}),
    cause:
      (body.message as string) ||
      (body.error as string) ||
      `Save failed (HTTP ${response.status})`,
  };
}

// ── Conversation Actions ─────────────────────────────────────────────────
// Wizard proxy for the toolbar admin actions API (CONVERSATION_ACTIONS_PLAN.md
// Section 5/6). Secrets are write-only: the GET returns hasSecret only, and the
// PUT includes `secret` only when the admin entered a new value.

export interface CatalogSummary {
  products: number;
  imported: number;
  skipped: number;
  duplicates: number;
  updatedAt: string;
}

export type ActionAuthType = 'none' | 'bearer' | 'header';

/**
 * Admin-configured extra order field. `agent` is a text field in the order
 * draft; `fixed` is shown read-only with its value; `hidden` never reaches the
 * agent and is added to the webhook payload on the server. Order in the array
 * is the display order in the draft.
 */
export type CustomFieldMode = 'agent' | 'fixed' | 'hidden';

export type CustomFieldType = 'text' | 'dropdown';

/** Text only: the value is read from the conversation's Genesys participant data. */
export type CustomFieldSource = 'participantData';

export interface CustomFieldOption {
  key: string;
  label: string;
}

export interface CustomField {
  /** /^[a-z0-9_]{1,32}$/, unique within the action. Webhook consumers map by it. */
  key: string;
  /** 1..40 chars; shown to agents exactly as typed. */
  label: string;
  mode: CustomFieldMode;
  /** agent: optional default. fixed / hidden: required, non-empty unless `source` is set (then the fallback). Dropdown: an option key. */
  value?: string;
  /** agent mode, or any mode with `source`. */
  required?: boolean;
  /** agent + text only, <= 80 chars. */
  placeholder?: string;
  /** Integer 1..500; default 100. */
  maxLength?: number;
  /** Omitted / `text` = text input. `dropdown` stores `options`. */
  type?: 'dropdown';
  /** Present only when `type === 'dropdown'`. 2..20 unique `{ key, label }`. */
  options?: CustomFieldOption[];
  source?: CustomFieldSource;
  /** Present only with `source`: the participant attribute name, exact (case-sensitive). */
  attributeKey?: string;
}

/** Custom field limits; mirror the toolbar admin API and the wizard backend. */
export const CUSTOM_FIELD_KEY_RE = /^[a-z0-9_]{1,32}$/;
export const CUSTOM_FIELD_KEY_MAX_LENGTH = 32;
export const CUSTOM_FIELD_LABEL_MAX_LENGTH = 40;
export const CUSTOM_FIELD_PLACEHOLDER_MAX_LENGTH = 80;
export const CUSTOM_FIELD_MAX_LENGTH_MIN = 1;
export const CUSTOM_FIELD_MAX_LENGTH_MAX = 500;
export const CUSTOM_FIELD_MAX_LENGTH_DEFAULT = 100;
/** agent + fixed fields (the ones agents see). */
export const CUSTOM_FIELDS_MAX_VISIBLE = 10;
export const CUSTOM_FIELDS_MAX_TOTAL = 20;
export const CUSTOM_FIELD_OPTIONS_MIN = 2;
export const CUSTOM_FIELD_OPTIONS_MAX = 20;
/**
 * Genesys participant attribute names (custom fields and Conversation info): any characters,
 * spaces included, trimmed at the ends. Genesys publishes no name limit; 500 is its Set
 * Participant Data value limit, and the toolbar's cap.
 */
export const PARTICIPANT_ATTRIBUTE_KEY_MAX_LENGTH = 500;
export const CUSTOM_FIELD_ATTRIBUTE_KEY_MAX_LENGTH = PARTICIPANT_ATTRIBUTE_KEY_MAX_LENGTH;

export type InfoFieldDisplay = 'value' | 'match' | 'link' | 'externalTag' | 'input';
export type InfoInputKind = 'text' | 'dropdown' | 'switch';
/** Which conversations a field shows on; `digital` is every non-voice channel. Absent on the wire = all. */
export type InfoFieldChannels = 'all' | 'voice' | 'digital';
export type InfoMatchOp = 'equals' | 'not_equals' | 'contains' | 'starts_with' | 'any' | 'gt' | 'gte' | 'lt' | 'lte';

/**
 * An exact condition is `{ equals, text }` (the v1 shape); `any` is `{ op, text }`;
 * the other operators are `{ op, value, text }`.
 */
export type InfoFieldMatch =
  | { equals: string; text: string; op?: undefined; value?: undefined }
  | { op: Exclude<InfoMatchOp, 'equals'>; value?: string; text: string; equals?: undefined };

/** Agents see a generic translated link text; a text stored by earlier pages is not read. */
export interface InfoFieldLink {
  /** Template; `{name}` placeholders are participant attribute names, after the host only. */
  url: string;
}

/**
 * What the agent edits on an `input` field. The extension writes the text, the option's `value` or
 * the switch's `onValue` / `offValue` to the customer participant's attribute.
 */
export type InfoFieldInput =
  | { kind: 'text'; maxLength: number }
  | { kind: 'dropdown'; options: Array<{ value: string; label?: string }> }
  | { kind: 'switch'; onValue: string; offValue: string };

/** One Conversation info field, as stored by the toolbar (see the wizard backend's utils/info-fields.ts). */
export interface InfoField {
  /** Minted by the toolbar; a new field is sent without one. */
  id?: string;
  label: string;
  /** Exact, case-sensitive Genesys participant attribute name; a link field's first placeholder. Absent for `externalTag`. */
  attributeKey?: string;
  display: InfoFieldDisplay;
  /** Present only when `display === 'match'`. */
  matches?: InfoFieldMatch[];
  /** Present on a link field. Always sent on save (null when not a link), so the toolbar never keeps a stale one. */
  link?: InfoFieldLink | null;
  /** Present on an agent input field only. */
  input?: InfoFieldInput;
  notify: boolean;
  /** Highlighted for agents. Always sent on save; omitted means the toolbar keeps the stored value. */
  important?: boolean;
  channels?: Exclude<InfoFieldChannels, 'all'>;
}

/** Conversation info limits; mirror the toolbar admin API and the wizard backend. */
export const INFO_FIELDS_MAX = 25;
export const INFO_FIELDS_MAX_IMPORTANT = 5;
export const INFO_FIELD_MATCHES_MAX = 10;
export const INFO_FIELD_LABEL_MAX_LENGTH = 40;
export const INFO_FIELD_ATTRIBUTE_KEY_MAX_LENGTH = PARTICIPANT_ATTRIBUTE_KEY_MAX_LENGTH;
/** A condition compares against a participant value, which Genesys caps at 500 characters. */
export const INFO_MATCH_EQUALS_MAX_LENGTH = 500;
/** A numeric condition's value. */
export const INFO_MATCH_NUMBER_MAX_LENGTH = 30;
/** The text agents see for a match: the extension's display cap of a shown value. */
export const INFO_MATCH_TEXT_MAX_LENGTH = 200;
export const INFO_LINK_URL_MAX_LENGTH = 1000;
export const INFO_LINK_PLACEHOLDERS_MAX = 5;
export const INFO_FIELD_ID_RE = /^inf_[0-9a-f]{8}$/;
/** Agent input fields per action. */
export const INFO_INPUTS_MAX = 5;
/** The longest text an agent can save, and a text field's default limit. */
export const INFO_INPUT_TEXT_MAX = 200;
export const INFO_INPUT_OPTIONS_MIN = 2;
export const INFO_INPUT_OPTIONS_MAX = 20;
/** A list option's or a switch's written value. */
export const INFO_INPUT_VALUE_MAX = 100;
export const INFO_INPUT_OPTION_LABEL_MAX = INFO_FIELD_LABEL_MAX_LENGTH;
/**
 * Sent with every action save: tells the wizard backend this page keeps link
 * fields, operator conditions, important marks and agent input fields, so its
 * list is safe to store.
 */
export const INFO_FIELDS_CLIENT_VERSION = 3;

/** Max webhook secret length; mirrors the toolbar admin API and the wizard backend. */
export const WEBHOOK_SECRET_MAX_LENGTH = 1024;

/**
 * Pointer returned with a 400 save failure; every part is optional.
 * CUSTOM_FIELDS_INVALID: `actionIndex`, `index`, `field`, `reason`.
 * INFO_FIELDS_INVALID: `index` (the action), `fieldIndex`, `conditionIndex`, `code`.
 * INFO_DIVISION_IN_USE: `index`, `conflictActionId`, `conflictLabel`, `divisionId`.
 */
export interface CustomFieldsErrorDetail {
  /** Set by the wizard backend; the toolbar admin API may omit it. */
  actionIndex?: number;
  /** Index into the action's customFields array (CUSTOM_FIELDS_INVALID); the action's index on the INFO_* codes. */
  index?: number;
  field?: string;
  /** Wizard backend: a sentence. Toolbar admin API: a snake_case code (e.g. `invalid_option_key`). */
  reason?: string;
  /** Index into the action's infoFields array. */
  fieldIndex?: number;
  /** Index into that field's conditions. */
  conditionIndex?: number;
  /** INFO_FIELDS_INVALID machine code (e.g. `too_many_important`). */
  code?: string;
  conflictActionId?: string;
  conflictLabel?: string;
  /** Null when the clash is through all divisions. */
  divisionId?: string | null;
}

/** True for a toolbar-style machine code; such a reason is not shown to the admin. */
export function isCustomFieldsReasonCode(reason: string): boolean {
  return /^[a-z0-9_]+$/.test(reason);
}

/** One instance as returned by GET /api/org-actions (sanitized, no secrets). */
export interface OrgActionInstance {
  actionId: string;
  type: string;
  label: string;
  enabled: boolean;
  webhookUrl: string;
  authType: ActionAuthType;
  hasSecret: boolean;
  headerName: string;
  timeoutMs: number;
  divisionIds: string[];
  /** Free-text description of how the org's customers order; '' when not set. */
  businessContext: string;
  /** [] when none are configured. */
  customFields: CustomField[];
  /** AI_REPLY fallback reply language (services/reply-languages.ts); '' when not set. */
  replyLanguage: string;
  /** CONVERSATION_INFO fields in display order; [] on other types. */
  infoFields: InfoField[];
  catalogSummary: CatalogSummary | null;
  /** Server-stamped on create; omitted on older instances. */
  createdAt?: string;
  /** Last successful test of the saved webhook settings; null when none or since changed. */
  webhookTestedAt: string | null;
  /** Admin-edit counter; sent back as `expectedVersion` when this action is saved or removed. */
  version: number;
}

/** Max length of `businessContext` after trim; mirrors the toolbar admin API. */
export const BUSINESS_CONTEXT_MAX_LENGTH = 300;

/** One instance as sent to PUT /api/org-actions (one action per save). */
export interface ActionInstanceSave {
  actionId?: string;
  /** `CREATE_ORDER` | `CREATE_RETURN` | `SEND_FILES` | `AI_REPLY` | `CONVERSATION_INFO` (see services/action-types.ts); validated by the wizard backend. */
  type: string;
  label: string;
  enabled: boolean;
  /** Webhook fields: sent on every type except AI_REPLY and CONVERSATION_INFO, which have no webhook. */
  webhookUrl?: string;
  authType?: ActionAuthType;
  secret?: string;
  headerName?: string;
  timeoutMs?: number;
  divisionIds: string[];
  /** An empty string clears the stored value. AI_REPLY: the reply instructions. Not sent for CONVERSATION_INFO. */
  businessContext?: string;
  /** Sent with the webhook fields; an empty array clears the stored list. */
  customFields?: CustomField[];
  /** AI_REPLY only, required there. */
  replyLanguage?: string;
  /** CONVERSATION_INFO only, always sent there in display order; an empty array clears the stored list. */
  infoFields?: InfoField[];
  /** The token from a successful test of these exact unsaved settings (Add action dialog or a saved card's form). */
  webhookTestToken?: string;
}

/** Settings typed in the Add action dialog, tested before the action exists (nothing is saved). */
export interface WebhookTestDraft {
  type: string;
  label: string;
  webhookUrl: string;
  authType: ActionAuthType;
  headerName?: string;
  timeoutMs: number;
  secret: string;
  customFields: CustomField[];
}

/**
 * A saved action's unsaved form values, tested with its `actionId` (nothing is
 * saved). Without `secret` the stored one is used.
 */
export interface WebhookTestForm {
  label: string;
  webhookUrl: string;
  authType: ActionAuthType;
  headerName?: string;
  timeoutMs: number;
  secret?: string;
  customFields: CustomField[];
}

export interface OrgActionsConfig {
  aiActionsEnabled: boolean;
  /**
   * Types a NEW action may use, as resolved by the wizard backend from the
   * toolbar. Read from the API so the picker follows the toolbar without a
   * local copy; a response without the field allows nothing (Add action is
   * disabled) rather than guessing.
   */
  allowedTypes: string[];
  /** Types whose use is charged to the org (toolbar `billed` flag); the rest are free. */
  billedTypes: string[];
  actions: OrgActionInstance[];
}

export interface CatalogImportSummary {
  products: number;
  imported: number;
  skipped: number;
  duplicates: number;
  errors: Array<{ line: number; reason: string }>;
}

export interface TestWebhookResult {
  statusCode: number | null;
  success: boolean;
  referenceId: string | null;
  message: string | null;
  /** Success only: pass back as `webhookTestToken` when saving the tested unsaved settings. */
  testToken: string | null;
}

function coerceTimestamp(value: unknown): string | undefined {
  if (typeof value === 'string' && value) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return new Date(value).toISOString();
  return undefined;
}

function parseCatalogSummary(raw: unknown): CatalogSummary | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Record<string, unknown>;
  return {
    products: typeof s.products === 'number' ? s.products : 0,
    imported: typeof s.imported === 'number' ? s.imported : 0,
    skipped: typeof s.skipped === 'number' ? s.skipped : 0,
    duplicates: typeof s.duplicates === 'number' ? s.duplicates : 0,
    updatedAt: coerceTimestamp(s.updatedAt) || '',
  };
}

const CUSTOM_FIELD_MODES: ReadonlySet<string> = new Set<CustomFieldMode>(['agent', 'fixed', 'hidden']);

/** Drop malformed entries instead of failing the load; the admin re-saves to normalize. */
function parseCustomFields(raw: unknown): CustomField[] {
  if (!Array.isArray(raw)) return [];
  const fields: CustomField[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const f = entry as Record<string, unknown>;
    if (typeof f.key !== 'string' || typeof f.label !== 'string') continue;
    if (typeof f.mode !== 'string' || !CUSTOM_FIELD_MODES.has(f.mode)) continue;
    const field: CustomField = { key: f.key, label: f.label, mode: f.mode as CustomFieldMode };
    if (typeof f.value === 'string') field.value = f.value;
    if (f.required === true) field.required = true;
    if (typeof f.placeholder === 'string') field.placeholder = f.placeholder;
    if (typeof f.maxLength === 'number' && Number.isFinite(f.maxLength)) field.maxLength = f.maxLength;
    if (f.type === 'dropdown' && Array.isArray(f.options)) {
      const options = f.options
        .filter((o): o is Record<string, unknown> => !!o && typeof o === 'object')
        .filter((o) => typeof o.key === 'string' && typeof o.label === 'string')
        .map((o) => ({ key: o.key as string, label: o.label as string }));
      if (options.length > 0) {
        field.type = 'dropdown';
        field.options = options;
      }
    }
    if (f.source === 'participantData' && field.type !== 'dropdown' && typeof f.attributeKey === 'string' && f.attributeKey) {
      field.source = 'participantData';
      field.attributeKey = f.attributeKey;
    }
    fields.push(field);
  }
  return fields;
}

const INFO_MATCH_OPS: ReadonlySet<string> = new Set(['not_equals', 'contains', 'starts_with', 'gt', 'gte', 'lt', 'lte']);

/** Drop malformed entries instead of failing the load (the wizard backend already applies the toolbar's read rules). */
function readInfoInput(raw: unknown): InfoFieldInput | null {
  if (!raw || typeof raw !== 'object') return null;
  const input = raw as Record<string, unknown>;
  if (input.kind === 'text') {
    return { kind: 'text', maxLength: typeof input.maxLength === 'number' ? input.maxLength : INFO_INPUT_TEXT_MAX };
  }
  if (input.kind === 'dropdown' && Array.isArray(input.options)) {
    const options = input.options
      .filter((o): o is Record<string, unknown> => !!o && typeof o === 'object' && typeof o.value === 'string')
      .map((o) => ({ value: o.value as string, ...(typeof o.label === 'string' && o.label ? { label: o.label } : {}) }));
    return { kind: 'dropdown', options };
  }
  if (input.kind === 'switch' && typeof input.onValue === 'string' && typeof input.offValue === 'string') {
    return { kind: 'switch', onValue: input.onValue, offValue: input.offValue };
  }
  return null;
}

export function readInfoFields(raw: unknown): InfoField[] {
  return parseInfoFields(raw);
}

function parseInfoFields(raw: unknown): InfoField[] {
  if (!Array.isArray(raw)) return [];
  const fields: InfoField[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const f = entry as Record<string, unknown>;
    if (typeof f.label !== 'string') continue;
    if (f.display !== 'value' && f.display !== 'match' && f.display !== 'link' && f.display !== 'externalTag' && f.display !== 'input') continue;
    if (f.display !== 'externalTag' && typeof f.attributeKey !== 'string') continue;
    const field: InfoField = {
      label: f.label,
      display: f.display,
      ...(f.display === 'externalTag' ? {} : { attributeKey: f.attributeKey as string }),
      notify: f.notify === true,
      important: f.important === true,
    };
    if (typeof f.id === 'string' && INFO_FIELD_ID_RE.test(f.id)) field.id = f.id;
    if (f.display === 'match' && Array.isArray(f.matches)) {
      field.matches = f.matches
        .filter((m): m is Record<string, unknown> => !!m && typeof m === 'object' && typeof m.text === 'string')
        .flatMap((m): InfoFieldMatch[] => {
          const text = m.text as string;
          if (m.op === undefined || m.op === null) return typeof m.equals === 'string' ? [{ equals: m.equals, text }] : [];
          if (m.op === 'any') return [{ op: 'any', text }];
          if (typeof m.op !== 'string' || !INFO_MATCH_OPS.has(m.op) || typeof m.value !== 'string') return [];
          return [{ op: m.op as Exclude<InfoMatchOp, 'equals'>, value: m.value, text }];
        });
    }
    if (f.display === 'link' && f.link && typeof f.link === 'object') {
      const link = f.link as Record<string, unknown>;
      if (typeof link.url === 'string') field.link = { url: link.url };
    }
    if (f.display === 'input') {
      const input = readInfoInput(f.input);
      if (!input) continue;
      field.input = input;
    }
    if (f.channels === 'voice' || f.channels === 'digital') field.channels = f.channels;
    fields.push(field);
  }
  return fields;
}

function parseCustomFieldsErrorDetail(raw: unknown): CustomFieldsErrorDetail | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const d = raw as Record<string, unknown>;
  const detail: CustomFieldsErrorDetail = {};
  const isIndex = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v);
  if (isIndex(d.actionIndex)) detail.actionIndex = d.actionIndex;
  if (isIndex(d.index)) detail.index = d.index;
  if (typeof d.field === 'string') detail.field = d.field;
  if (typeof d.reason === 'string') detail.reason = d.reason;
  if (isIndex(d.fieldIndex)) detail.fieldIndex = d.fieldIndex;
  if (isIndex(d.conditionIndex)) detail.conditionIndex = d.conditionIndex;
  if (typeof d.code === 'string') detail.code = d.code;
  if (typeof d.conflictActionId === 'string') detail.conflictActionId = d.conflictActionId;
  if (typeof d.conflictLabel === 'string') detail.conflictLabel = d.conflictLabel;
  if (typeof d.divisionId === 'string' || d.divisionId === null) detail.divisionId = d.divisionId;
  return detail;
}

function parseOrgActionInstances(raw: unknown): OrgActionInstance[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((a): a is Record<string, unknown> => !!a && typeof a === 'object')
    .map((a): OrgActionInstance => {
      const createdAt = coerceTimestamp(a.createdAt);
      return {
        actionId: typeof a.actionId === 'string' ? a.actionId : '',
        type: typeof a.type === 'string' ? a.type : 'CREATE_ORDER',
        label: typeof a.label === 'string' ? a.label : '',
        enabled: a.enabled === true,
        webhookUrl: typeof a.webhookUrl === 'string' ? a.webhookUrl : '',
        authType: (a.authType === 'bearer' || a.authType === 'header' ? a.authType : 'none') as ActionAuthType,
        hasSecret: a.hasSecret === true,
        headerName: typeof a.headerName === 'string' ? a.headerName : '',
        timeoutMs: typeof a.timeoutMs === 'number' ? a.timeoutMs : 10000,
        divisionIds: Array.isArray(a.divisionIds)
          ? a.divisionIds.filter((d): d is string => typeof d === 'string')
          : [],
        businessContext: typeof a.businessContext === 'string' ? a.businessContext : '',
        customFields: parseCustomFields(a.customFields),
        replyLanguage: typeof a.replyLanguage === 'string' ? a.replyLanguage : '',
        infoFields: parseInfoFields(a.infoFields),
        catalogSummary: parseCatalogSummary(a.catalogSummary),
        ...(createdAt ? { createdAt } : {}),
        webhookTestedAt: typeof a.webhookTestedAt === 'string' && Number.isFinite(Date.parse(a.webhookTestedAt))
          ? a.webhookTestedAt
          : null,
        version: typeof a.version === 'number' && Number.isInteger(a.version) && a.version >= 0 ? a.version : 0,
      };
    })
    .filter((a) => a.actionId);
}

/** Load the org's Conversation Actions configuration. */
export async function getOrgActions(
  genesysOrgId: string,
  genesysRegion: string,
): Promise<OrgActionsConfig> {
  const params = new URLSearchParams({ genesysOrgId, genesysRegion });
  const response = await fetchWithAuth(`${API_BASE}/api/org-actions?${params.toString()}`);
  const body = await safeJson(response);

  if (!response.ok) {
    // Defense in depth: a backend that still reports the disabled state as a
    // 403 must land in the calm disabled branch, not the error branch.
    if (body.error === 'ACTIONS_DISABLED' || body.error === 'CONVERSATIONS_NOT_ENABLED') {
      return { aiActionsEnabled: false, allowedTypes: [], billedTypes: [], actions: [] };
    }
    throw new Error(
      (body.message as string) || (body.error as string) || `Load actions failed (HTTP ${response.status})`,
    );
  }

  const actions = parseOrgActionInstances(body.actions);
  const allowedTypes = Array.isArray(body.allowedTypes)
    ? body.allowedTypes.filter((type): type is string => typeof type === 'string')
    : [];
  const billedTypes = Array.isArray(body.billedTypes)
    ? body.billedTypes.filter((type): type is string => typeof type === 'string')
    : [];

  return {
    aiActionsEnabled: body.aiActionsEnabled !== false,
    allowedTypes,
    billedTypes,
    actions,
  };
}

export interface OrgActionSaveFailure {
  status: false;
  cause: string;
  code?: string;
  detail?: CustomFieldsErrorDetail;
}

async function putOrgAction(
  genesysOrgId: string,
  genesysRegion: string,
  body: Record<string, unknown>,
): Promise<{ ok: true; body: Record<string, unknown> } | OrgActionSaveFailure> {
  const params = new URLSearchParams({ genesysOrgId, genesysRegion });
  const response = await fetchWithAuth(`${API_BASE}/api/org-actions?${params.toString()}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  const parsed = await safeJson(response);
  if (response.ok && parsed.success) return { ok: true, body: parsed };

  // The wizard backend's own errors carry `detail`; a toolbar 400 carries `details`.
  const detail = parseCustomFieldsErrorDetail(parsed.detail ?? parsed.details);
  return {
    status: false,
    // Machine-readable code (wizard backend or toolbar admin API) so the UI
    // can show a translated message for known failures. ACTIONS_CONFLICT carries
    // `reason` "version" (changed in another window) or "deleted".
    ...(typeof parsed.error === 'string' ? { code: parsed.error } : {}),
    ...(detail ? { detail } : {}),
    cause:
      (parsed.message as string) ||
      (parsed.error as string) ||
      `Save failed (HTTP ${response.status})`,
  };
}

/**
 * Create (no `actionId`) or update one action. `expectedVersion` is the
 * `version` the page loaded, required on an update; the response is the stored
 * instance with its new version.
 */
export async function saveOrgAction(
  genesysOrgId: string,
  genesysRegion: string,
  action: ActionInstanceSave,
  expectedVersion?: number,
): Promise<{ status: true; action: OrgActionInstance } | OrgActionSaveFailure> {
  const result = await putOrgAction(genesysOrgId, genesysRegion, {
    action,
    ...(expectedVersion !== undefined ? { expectedVersion } : {}),
    infoFieldsVersion: INFO_FIELDS_CLIENT_VERSION,
  });
  if (!('ok' in result)) return result;
  const [saved] = parseOrgActionInstances([result.body.action]);
  return saved ? { status: true, action: saved } : { status: false, cause: 'Save failed (no action in the response)' };
}

/** Remove one action, checked against the `version` the page loaded. */
export async function deleteOrgAction(
  genesysOrgId: string,
  genesysRegion: string,
  actionId: string,
  expectedVersion: number,
): Promise<{ status: true } | OrgActionSaveFailure> {
  const result = await putOrgAction(genesysOrgId, genesysRegion, { deleteActionId: actionId, expectedVersion });
  return 'ok' in result ? { status: true } : result;
}

/** Where a catalog upload or download is; `percent` is null while the server works. */
export interface CatalogTransferProgress {
  phase: 'uploading' | 'importing' | 'downloading';
  percent: number | null;
}

/** Codes for transfer failures the wizard detects itself (S3 PUT / GET from the browser). */
export const CATALOG_TRANSFER_FAILED = 'CATALOG_TRANSFER_FAILED';

const CATALOG_UPLOAD_TIMEOUT_MS = 5 * 60 * 1000;

/** PUT the file to the presigned S3 URL, reporting upload progress. Resolves the HTTP status (0 = network error). */
function putCatalogFile(
  url: string,
  headers: Record<string, string>,
  file: File,
  onProgress: (percent: number) => void,
): Promise<number> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    for (const [name, value] of Object.entries(headers)) xhr.setRequestHeader(name, value);
    xhr.timeout = CATALOG_UPLOAD_TIMEOUT_MS;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) onProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)));
    };
    xhr.onload = () => resolve(xhr.status);
    xhr.onerror = () => resolve(0);
    xhr.ontimeout = () => resolve(0);
    xhr.onabort = () => resolve(0);
    xhr.send(file);
  });
}

function stringHeaders(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== 'object') return {};
  return Object.fromEntries(
    Object.entries(raw as Record<string, unknown>).filter((e): e is [string, string] => typeof e[1] === 'string'),
  );
}

/**
 * Upload a product catalog CSV (already validated client-side): ask for a
 * presigned URL, PUT the file straight to S3 (the org-region bucket), then
 * have the admin API import it. Validation and the summary stay server-side.
 */
export async function uploadActionCatalog(
  genesysOrgId: string,
  genesysRegion: string,
  actionId: string,
  file: File,
  onProgress: (progress: CatalogTransferProgress) => void = () => {},
): Promise<{ status: boolean; summary?: CatalogImportSummary; cause?: string; code?: string }> {
  const params = new URLSearchParams({ genesysOrgId, genesysRegion });
  const catalogUrl = `${API_BASE}/api/org-actions/catalog?${params.toString()}`;
  const failure = (body: Record<string, unknown>, status: number) => ({
    status: false,
    ...(typeof body.error === 'string' ? { code: body.error } : {}),
    cause: (body.message as string) || (body.error as string) || `Catalog import failed (HTTP ${status})`,
  });

  onProgress({ phase: 'uploading', percent: 0 });
  const urlResponse = await fetchWithAuth(catalogUrl, {
    method: 'POST',
    body: JSON.stringify({ actionId, step: 'upload-url', fileName: file.name, sizeBytes: file.size }),
  });
  const urlBody = await safeJson(urlResponse);
  if (!urlResponse.ok || typeof urlBody.uploadUrl !== 'string' || typeof urlBody.uploadId !== 'string') {
    return failure(urlBody, urlResponse.status);
  }

  const putStatus = await putCatalogFile(
    urlBody.uploadUrl,
    stringHeaders(urlBody.uploadHeaders),
    file,
    (percent) => onProgress({ phase: 'uploading', percent }),
  );
  if (putStatus < 200 || putStatus >= 300) {
    return { status: false, code: CATALOG_TRANSFER_FAILED };
  }

  onProgress({ phase: 'importing', percent: null });
  const response = await fetchWithAuth(catalogUrl, {
    method: 'POST',
    body: JSON.stringify({ actionId, step: 'import', uploadId: urlBody.uploadId, fileName: file.name }),
  });
  const body = await safeJson(response);

  if (response.ok && body.success !== false) {
    // The toolbar may wrap the summary or return it flat; accept both.
    const raw = (body.summary && typeof body.summary === 'object' ? body.summary : body) as Record<string, unknown>;
    return {
      status: true,
      summary: {
        products: typeof raw.products === 'number' ? raw.products : 0,
        imported: typeof raw.imported === 'number' ? raw.imported : 0,
        skipped: typeof raw.skipped === 'number' ? raw.skipped : 0,
        duplicates: typeof raw.duplicates === 'number' ? raw.duplicates : 0,
        errors: Array.isArray(raw.errors)
          ? raw.errors
            .filter((e): e is Record<string, unknown> => !!e && typeof e === 'object')
            .map((e) => ({
              line: typeof e.line === 'number' ? e.line : 0,
              reason: typeof e.reason === 'string' ? e.reason : '',
            }))
          : [],
      },
    };
  }

  return failure(body, response.status);
}

export interface ActiveCatalog {
  importedAt: string;
  products: Array<Record<string, unknown>>;
}

/** Thrown when the browser could not fetch the catalog file from S3. */
export class CatalogTransferError extends Error {
  readonly code = CATALOG_TRANSFER_FAILED;
}

/** Read a response body as text, reporting progress against the expected size. */
async function readWithProgress(response: Response, expectedBytes: number, onPercent: (percent: number) => void): Promise<string> {
  const total = Number(response.headers.get('content-length')) || expectedBytes;
  if (!response.body || !total) return response.text();
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let received = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.length;
    onPercent(Math.min(100, Math.round((received / total) * 100)));
  }
  const bytes = new Uint8Array(received);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.length;
  }
  return new TextDecoder('utf-8').decode(bytes);
}

/**
 * Fetch the currently active parsed catalog for download: a presigned S3 URL
 * from the admin API, then the JSON straight from the org-region bucket. A
 * toolbar that predates `delivery=url` answers the JSON itself, used as is.
 */
export async function downloadActionCatalog(
  genesysOrgId: string,
  genesysRegion: string,
  actionId: string,
  onProgress: (progress: CatalogTransferProgress) => void = () => {},
): Promise<ActiveCatalog> {
  const params = new URLSearchParams({ genesysOrgId, genesysRegion, actionId, delivery: 'url' });
  onProgress({ phase: 'downloading', percent: null });
  const response = await fetchWithAuth(`${API_BASE}/api/org-actions/catalog?${params.toString()}`);
  const body = await safeJson(response);

  if (!response.ok) {
    throw new Error(
      (body.message as string) || (body.error as string) || `Catalog download failed (HTTP ${response.status})`,
    );
  }

  let catalogBody: Record<string, unknown> = body;
  if (typeof body.downloadUrl === 'string') {
    let file: Response;
    try {
      // Plain GET: the presigned URL carries its own authorization.
      file = await fetch(body.downloadUrl);
    } catch {
      throw new CatalogTransferError('Catalog download failed');
    }
    if (!file.ok) throw new CatalogTransferError(`Catalog download failed (HTTP ${file.status})`);
    const expectedBytes = typeof body.sizeBytes === 'number' ? body.sizeBytes : 0;
    let text: string;
    try {
      text = await readWithProgress(file, expectedBytes, (percent) => onProgress({ phase: 'downloading', percent }));
    } catch {
      throw new CatalogTransferError('Catalog download failed');
    }
    try {
      catalogBody = { catalog: JSON.parse(text) };
    } catch {
      throw new Error('Unexpected catalog file');
    }
  }

  // Accept the catalog either flat or wrapped in a `catalog` envelope.
  const raw = (catalogBody.catalog && typeof catalogBody.catalog === 'object' ? catalogBody.catalog : catalogBody) as Record<string, unknown>;
  return {
    importedAt: typeof raw.importedAt === 'string' ? raw.importedAt : '',
    products: Array.isArray(raw.products)
      ? raw.products.filter((p): p is Record<string, unknown> => !!p && typeof p === 'object')
      : [],
  };
}

/** Wizard table + search is hidden at or above this product count. */
export const CATALOG_ROW_UI_GATE = 500;
/** Rows requested and shown per search; the API's `total` says how many matched. */
export const CATALOG_SEARCH_LIMIT = 25;

export interface CatalogProductRow {
  sku: string;
  productName: string;
  description: string;
  unit: string | null;
  packageSize: number | null;
  aliases: string[];
  active: boolean;
}

export interface CatalogProductInput {
  sku: string;
  productName: string;
  description?: string;
  unit?: string;
  packageSize?: number | null;
  aliases?: string;
  active?: boolean;
  overwrite?: boolean;
  /** Edit mode only: a SKU deleted meanwhile is 404 SKU_NOT_FOUND instead of a re-create. */
  expectExisting?: boolean;
}

export interface CatalogRowError {
  status: number;
  code?: string;
  message: string;
  productName?: string;
  sku?: string;
}

function catalogRowError(response: Response, body: Record<string, unknown>): CatalogRowError {
  const details = (body.details && typeof body.details === 'object' ? body.details : null)
    || (body.detail && typeof body.detail === 'object' ? body.detail : null)
    || {};
  const detail = details as Record<string, unknown>;
  return {
    status: response.status,
    code: typeof body.error === 'string' ? body.error : undefined,
    message:
      (typeof body.message === 'string' && body.message)
      || (typeof body.error === 'string' && body.error)
      || `Catalog request failed (HTTP ${response.status})`,
    ...(typeof detail.productName === 'string' ? { productName: detail.productName } : {}),
    ...(typeof detail.sku === 'string' ? { sku: detail.sku } : {}),
  };
}

function parseCatalogProductRow(raw: unknown): CatalogProductRow | null {
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  if (typeof p.sku !== 'string' || !p.sku) return null;
  if (typeof p.productName !== 'string' || !p.productName) return null;
  return {
    sku: p.sku,
    productName: p.productName,
    description: typeof p.description === 'string' ? p.description : '',
    unit: typeof p.unit === 'string' && p.unit ? p.unit : null,
    packageSize: typeof p.packageSize === 'number' && Number.isFinite(p.packageSize) ? p.packageSize : null,
    aliases: Array.isArray(p.aliases) ? p.aliases.filter((a): a is string => typeof a === 'string' && a.length > 0) : [],
    active: p.active !== false,
  };
}

export async function upsertActionCatalogProduct(
  genesysOrgId: string,
  genesysRegion: string,
  actionId: string,
  product: CatalogProductInput,
): Promise<{ ok: true; created: boolean; product: CatalogProductRow; catalogSummary: CatalogSummary } | { ok: false; error: CatalogRowError }> {
  const params = new URLSearchParams({ genesysOrgId, genesysRegion });
  const response = await fetchWithAuth(`${API_BASE}/api/org-actions/catalog/product?${params.toString()}`, {
    method: 'PUT',
    body: JSON.stringify({ actionId, ...product }),
  });
  const body = await safeJson(response);
  if (response.ok && body.success !== false) {
    const productRow = parseCatalogProductRow(body.product);
    const catalogSummary = parseCatalogSummary(body.catalogSummary);
    if (productRow && catalogSummary) {
      return {
        ok: true,
        created: body.created === true,
        product: productRow,
        catalogSummary,
      };
    }
  }
  return { ok: false, error: catalogRowError(response, body) };
}

export async function deleteActionCatalogProduct(
  genesysOrgId: string,
  genesysRegion: string,
  actionId: string,
  sku: string,
): Promise<{ ok: true; catalogSummary: CatalogSummary } | { ok: false; error: CatalogRowError }> {
  // SKU in the query string, not the path: the HTTP API decodes path
  // parameters twice, so a SKU with a literal `%` would not survive.
  const params = new URLSearchParams({ genesysOrgId, genesysRegion, actionId, sku });
  const response = await fetchWithAuth(
    `${API_BASE}/api/org-actions/catalog/product?${params.toString()}`,
    { method: 'DELETE' },
  );
  const body = await safeJson(response);
  if (response.ok && body.success !== false) {
    const catalogSummary = parseCatalogSummary(body.catalogSummary);
    if (catalogSummary) return { ok: true, catalogSummary };
  }
  return { ok: false, error: catalogRowError(response, body) };
}

export async function searchActionCatalog(
  genesysOrgId: string,
  genesysRegion: string,
  actionId: string,
  q: string,
  opts: { limit?: number; cursor?: string } = {},
): Promise<{ ok: true; results: CatalogProductRow[]; nextCursor: string | null; total: number } | { ok: false; error: CatalogRowError }> {
  const params = new URLSearchParams({ genesysOrgId, genesysRegion, actionId, q });
  params.set('limit', String(opts.limit || CATALOG_SEARCH_LIMIT));
  if (opts.cursor) params.set('cursor', opts.cursor);
  const response = await fetchWithAuth(`${API_BASE}/api/org-actions/catalog/search?${params.toString()}`);
  const body = await safeJson(response);
  if (response.ok && body.success !== false) {
    const results = Array.isArray(body.results)
      ? body.results.map(parseCatalogProductRow).filter((p): p is CatalogProductRow => p !== null)
      : [];
    return {
      ok: true,
      results,
      nextCursor: typeof body.nextCursor === 'string' ? body.nextCursor : null,
      // An older toolbar omits `total`; fall back to the page size.
      total: typeof body.total === 'number' && Number.isFinite(body.total) ? body.total : results.length,
    };
  }
  return { ok: false, error: catalogRowError(response, body) };
}

/**
 * Send a sample delivery through the full webhook path (works while disabled):
 * the saved settings of `actionId`, or a `draft` typed in the Add action dialog.
 */
export async function testActionWebhook(
  genesysOrgId: string,
  genesysRegion: string,
  target: { actionId: string } | { draft: WebhookTestDraft } | { actionId: string; draft: WebhookTestForm },
): Promise<TestWebhookResult> {
  const params = new URLSearchParams({ genesysOrgId, genesysRegion });
  const response = await fetchWithAuth(`${API_BASE}/api/org-actions/test-webhook?${params.toString()}`, {
    method: 'POST',
    body: JSON.stringify(target),
  });
  const body = await safeJson(response);

  if (!response.ok && typeof body.statusCode !== 'number' && body.success === undefined) {
    throw new Error(
      (body.message as string) || (body.error as string) || `Webhook test failed (HTTP ${response.status})`,
    );
  }

  return {
    statusCode: typeof body.statusCode === 'number' ? body.statusCode : null,
    success: body.success === true,
    referenceId: typeof body.referenceId === 'string' ? body.referenceId : null,
    message: typeof body.message === 'string' ? body.message : null,
    testToken: body.success === true && typeof body.testToken === 'string' && body.testToken ? body.testToken : null,
  };
}

/**
 * One row from GET /api/org-actions/deliveries (identifiers and counts only).
 * Rows carry status 'delivered' or 'failed'; legacy rows have no status
 * attribute and are treated as delivered. failureCode is set on failed rows
 * only (e.g. WEBHOOK_FAILED, WEBHOOK_TIMEOUT, AUTH_FAILED, VALIDATION_FAILED).
 * itemCount is order or return lines, or files for a SEND_FILES action.
 * agentName and referenceId (the customer endpoint's own reference) are
 * optional: shown when the toolbar deliveries API returns them.
 */
export interface ActionDelivery {
  submittedAt: string;
  actionId: string;
  eventId: string;
  itemCount: number | null;
  status: string;
  failureCode: string | null;
  agentName: string | null;
  referenceId: string | null;
}

/** Rolling window shown on the Recent deliveries card. */
const DELIVERIES_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

function isWithinDeliveriesWindow(submittedAt: string, now = Date.now()): boolean {
  const ts = Date.parse(submittedAt);
  return Number.isFinite(ts) && ts <= now && now - ts <= DELIVERIES_WINDOW_MS;
}

/** Load the org's recent webhook deliveries, most recent first, last seven days. */
export async function getOrgActionDeliveries(
  genesysOrgId: string,
  genesysRegion: string,
  limit?: number,
): Promise<ActionDelivery[]> {
  const params = new URLSearchParams({ genesysOrgId, genesysRegion });
  if (limit) params.set('limit', String(limit));
  const response = await fetchWithAuth(`${API_BASE}/api/org-actions/deliveries?${params.toString()}`);
  const body = await safeJson(response);

  if (!response.ok) {
    throw new Error(
      (body.message as string) || (body.error as string) || `Load deliveries failed (HTTP ${response.status})`,
    );
  }

  const now = Date.now();
  const rows = Array.isArray(body.deliveries) ? body.deliveries : [];
  return rows
    .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
    .map((r) => ({
      submittedAt: typeof r.submittedAt === 'string' ? r.submittedAt : '',
      actionId: typeof r.actionId === 'string' ? r.actionId : '',
      eventId: typeof r.eventId === 'string' ? r.eventId : '',
      itemCount: typeof r.itemCount === 'number' ? r.itemCount : null,
      status: typeof r.status === 'string' ? r.status : 'delivered',
      failureCode: typeof r.failureCode === 'string' ? r.failureCode : null,
      agentName: typeof r.agentName === 'string' && r.agentName.trim() ? r.agentName.trim() : null,
      referenceId: typeof r.referenceId === 'string' && r.referenceId.trim() ? r.referenceId.trim() : null,
    }))
    .filter((r) => isWithinDeliveriesWindow(r.submittedAt, now));
}

/**
 * Update credentials (and optionally dataRegion) for a pre-provisioned organization.
 * The backend reads the org's name and region from the record stored for
 * `genesysOrgId`; `organization` / `region` in the payload are not used.
 */
export async function updateOrgCredentials(
  payload: { organization: string; region: string; client_id: string; client_secret: string; dataRegion?: string },
  genesysOrgId: string,
  genesysRegion: string,
): Promise<{ status: boolean; cause?: string }> {
  const params = new URLSearchParams({ genesysOrgId, genesysRegion });
  const response = await fetchWithAuth(`${API_BASE}/api/update-credentials?${params.toString()}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });

  const body = await safeJson(response);

  if (response.ok && body.success) {
    return { status: true };
  }

  return {
    status: false,
    cause: (body.message as string) || (body.error as string) || `Credential update failed (HTTP ${response.status})`,
  };
}

/**
 * Unregister organization from MediaBriz backend.
 * Uses genesysOrgId for reliable lookup, consistent with checkOrganization.
 * 404 is treated as success (org was never registered or already removed).
 */
export async function unregisterOrganization(
  genesysOrgId: string,
  genesysRegion: string,
): Promise<void> {
  const params = new URLSearchParams({ genesysOrgId, genesysRegion });
  const response = await fetchWithAuth(`${API_BASE}/api/unregister-org?${params.toString()}`, {
    method: 'DELETE',
  });

  if (response.status === 404) {
    return;
  }

  if (!response.ok) {
    const body = await safeJson(response);
    throw new Error((body.message as string) || `Unregister failed (HTTP ${response.status})`);
  }
}
